import { supabase } from '@/lib/supabase'

export type DbHealth = {
  assetsActive: number
  monitorsActive: number
  opportunitiesActive: number
  candlesByTf: Array<{ timeframe: string; rows: number; latest: string | null }>
}

export async function fetchDbHealth(): Promise<DbHealth> {
  const [assets, monitors, opps] = await Promise.all([
    supabase.from('crypto_assets').select('id', { count: 'planned', head: true }).eq('is_active', true),
    supabase.from('active_monitoring').select('id', { count: 'planned', head: true }).eq('status', 'active'),
    // Ajuste: contar oportunidades sem depender de coluna 'status'
    supabase.from('trading_opportunities').select('id', { count: 'planned', head: true }),
  ])
  const timeframes = ['5','15','30','60','240','720','D','W','M']
  const candlesByTf: Array<{ timeframe: string; rows: number; latest: string | null }> = []
  for (const tf of timeframes) {
    const [cnt, latest] = await Promise.all([
      supabase.from('ohlcv_candles').select('id', { head: true, count: 'planned' }).eq('timeframe', tf),
      supabase.from('ohlcv_candles').select('open_time').eq('timeframe', tf).order('open_time', { ascending: false }).limit(1),
    ])
    candlesByTf.push({ timeframe: tf, rows: cnt.count ?? 0, latest: (latest.data as any[] | null)?.[0]?.open_time ?? null })
  }
  return {
    assetsActive: assets.count ?? 0,
    monitorsActive: monitors.count ?? 0,
    opportunitiesActive: opps.count ?? 0,
    candlesByTf,
  }
}

export type BybitRateConfig = {
  reqPerSecond: number
  reqPerMinute: number
}

// Keep in sync with supabase/functions/shared/constants.ts (SSOT no backend)
export const DEFAULT_BYBIT_RATE: BybitRateConfig = { reqPerSecond: 2, reqPerMinute: 120 }

export function estimateCapacity(params: {
  health: DbHealth
  bybit: BybitRateConfig
  reqPerMonitorPerMin: number
}): { currentPerMin: number; remainingPerMin: number; avgMonitorsPerAsset: number; additionalAssetsPossible: number } {
  const { health, bybit, reqPerMonitorPerMin } = params
  const avgMonitorsPerAsset = health.assetsActive > 0 ? health.monitorsActive / health.assetsActive : 1
  const currentPerMin = health.monitorsActive * reqPerMonitorPerMin
  const remainingPerMin = Math.max(bybit.reqPerMinute - currentPerMin, 0)
  const perAssetCost = Math.max(avgMonitorsPerAsset * reqPerMonitorPerMin, 0.0001)
  const additionalAssetsPossible = Math.floor(remainingPerMin / perAssetCost)
  return { currentPerMin, remainingPerMin, avgMonitorsPerAsset, additionalAssetsPossible }
}
